<template>
  <li
    class="p-10 md:p-16 flex flex-col justify-between relative card"
    :class="{'pt-0 bg-white recommend_popular': isPopular || isRecommeded}"
  >
    <div class="py-4 md:py-8 block text-primary text-2xl popular-area">
      <span v-if="isPopular">
        {{ $t('most_popular') }}
      </span>
      <nv-icon v-if="isPopular">
        bookmark
      </nv-icon>
    </div>
    <div>

      <header class="my-16 block">
        <span class="block h5 mb-3">{{ name }}</span>
        <!-- Price -->
        <i18n-t
          keypath="price_desc"
          tag="div"
          class="block mb-3 text-2xl"
        >
          <template #value>
            <span class="nv-m-0">
              {{ getPrice }}
            </span>
          </template>
          <template #period>
            <span class="">
              {{ $t(`BILLING_${billCycle.toUpperCase()}_CYCLE`, { count: billInterval }) }}
            </span>
          </template>
        </i18n-t>
        <span v-if="billInterval > 1 || billCycle === 'year'" class="text-primary">
         {{ $t('PRICE_PER_CYCLE', { cycle: $t('month'), amount: pricePerCycle }) }} 
        </span>
      </header>
      <section class="block my-10">

      <!-- Features -->
      <ul class="flex flex-col gap-3" v-if="features">
        <li v-for="feature in features" class="flex items-center gap-2">
          <nv-icon>
            tick
          </nv-icon>
          {{ feature }}
        </li>
      </ul>

      </section>
    </div>
    <footer class="block md:mt-16">
      <a :href="externalBuyAction" v-if="externalBuyAction" class="w-full">
        <button class="fira-ui-btn w-full" :class="{'primary': isPopular || isRecommeded, 'white': !(isPopular || isRecommeded)}" >
          {{ $t('BUY_NOW') }}
        </button>
      </a>
      <button v-else class="fira-ui-btn w-full" :class="{'primary': isPopular || isRecommeded, 'white': !(isPopular || isRecommeded)}" >
          {{ $t('BUY_NOW') }}
        </button>
    </footer>
  </li>
</template>
<script lang="ts" setup>
import formatMoney from '@/utils/formatMoney';
import type { IVariantRoot } from '@nuinalp/global-website-sdk-js/types/types/variants';
import type { PropType } from 'vue';

const props = defineProps({
  isRecommeded: { value: false, type: Boolean, required: false },
  limitedOffer: { value: false, type: Boolean, required: false },
  actionText: { default: '', type: String, required: false },
  name: { type: String, required: true },
  isPopular: { value: false, type: Boolean, required: false },
  showRefoundPolicy: { default: true, type: Boolean, required: false },
  showGetButton: { default: true, type: Boolean, required: false },
  billCycle: { default: 'month', type: String, required: true },
  billInterval: { type: Number, required: false, default: () => 1 },
  saveDiscount: { default: undefined, type: Number, required: false },
  value: { default: 0, type: Number, required: true },
  netValue: { default: 0, type: Number, required: true },
  onGetPlan: { default: () => null, type: Function, required: false },
  showRenewAlert: { type: Boolean, required: false, default: () => false },
  features: { type: Array as PropType<IVariantRoot['features']>, required: false, default: () => [] },
  externalBuyAction: { type: String, required: false, default: () => null },
});

// ---------------------------------
// Hooks
// ---------------------------------
const { $formatPrice } = useNuxtApp();
const { t, locale } = useI18n()!;


// ---------------------------------
// State
// ---------------------------------

const getTotal = computed(() => t(
  'plan_total',
  { total: formatMoney(props.netValue, locale.value) },
));

const pricePerCycle = computed(() => {
  const period = props.billCycle === 'year' ? 12 * props.billInterval : props.billInterval
  return formatMoney((props.netValue) / 100 / period, locale.value)
})

const getPrice = computed(() => formatMoney(
  (props.value <= 0 ? 0 : props.netValue) / 100,
  locale.value,
));

const getRenewInstallmentsValue = computed(() => formatMoney(
  props.value <= 0 ? 0 : props.value / props.billInterval,
  locale.value,
));

const billCycleAlert = computed(() => {
  if (props.billCycle === 'month' && props.billInterval >= 12) {
    return t('bill_cycle_desc', { cycle: t('period_subscription_desc_year') });
  }

  return t('bill_cycle_desc', { cycle: t(`period_subscription_desc_${props.billCycle}`) });
});

const renewAlert = computed(() => t('renew_alert', {
  net_value: $formatPrice(props.netValue),
  renew_alert_period: t(
    'renew_alert_period',
    {
      bill_cycle: t(`${props.billCycle}_plan`, props.billInterval),
      bill_interval: props.billInterval,
    },
    props.billInterval,
  ),
  renew_value: $formatPrice(props.value),
  installments: t(
    'installments_desc',
    {
      quantity: props.billInterval,
      price_desc: getRenewInstallmentsValue.value,
    },
  ),
}));
</script>
<style lang="scss" scoped>
.popular-area {
  nv-icon {
    color: var(--nv-theme-primary);
    --font-size: 90px;
    position: absolute;
    top: -30px;
    right: 10px;
  }
}

@media (screen(md)) {
  .popular-area {
    nv-icon {
      right: 2rem;
    }
  }
}

li.card {
  outline: 1px dashed black;
}

.recommend_popular li nv-icon {
  color: var(--nv-theme-primary);
}
</style>